import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { API_BASE_URL } from '../../config/api';
import './LocationCategoryPage.css';

const LocationCategoryPage = () => {
  // Data states - initialized as empty arrays, will be fetched from API
  const [businessTypes, setBusinessTypes] = useState([]);
  const [salesCategories, setSalesCategories] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [gccCountries, setGccCountries] = useState([]);
  const [otherCountries, setOtherCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalMode, setModalMode] = useState('add');
  const [editItem, setEditItem] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    code: ''
  });

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBusinessTypes(),
        fetchSalesCategories(),
        fetchProjectCategories(),
        fetchGccCountries(),
        fetchOtherCountries(),
        fetchCategories()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/business-types`);
      if (response.ok) {
        const data = await response.json();
        setBusinessTypes(data);
      }
    } catch (error) {
      console.error('Error fetching business types:', error);
    }
  };

  const fetchSalesCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/sales-categories`);
      if (response.ok) {
        const data = await response.json();
        setSalesCategories(data);
      }
    } catch (error) {
      console.error('Error fetching sales categories:', error);
    }
  };

  const fetchProjectCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/project-categories`);
      if (response.ok) {
        const data = await response.json();
        setProjectCategories(data);
      }
    } catch (error) {
      console.error('Error fetching project categories:', error);
    }
  };

  const fetchGccCountries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/locations/gcc`);
      if (response.ok) {
        const data = await response.json();
        setGccCountries(data);
      }
    } catch (error) {
      console.error('Error fetching GCC countries:', error);
    }
  };

  const fetchOtherCountries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/locations/other`);
      if (response.ok) {
        const data = await response.json();
        setOtherCountries(data);
      }
    } catch (error) {
      console.error('Error fetching other countries:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Open modal for adding
  const openAddModal = (type, parent = null) => {
    setModalType(type);
    setModalMode('add');
    setEditItem(null);
    setParentCategory(parent);
    setFormData({ name: '', image: '', code: '' });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      let endpoint = '';
      let refreshFn = null;

      switch (type) {
        case 'category':
          endpoint = `${API_BASE_URL}/categories/${id}`;
          refreshFn = fetchCategories;
          break;
        case 'subcategory':
          endpoint = `${API_BASE_URL}/categories/subcategory/${id}`;
          refreshFn = fetchCategories;
          break;
        case 'businessType':
          endpoint = `${API_BASE_URL}/settings/business-types/${id}`;
          refreshFn = fetchBusinessTypes;
          break;
        case 'salesCategory':
          endpoint = `${API_BASE_URL}/settings/sales-categories/${id}`;
          refreshFn = fetchSalesCategories;
          break;
        case 'projectCategory':
          endpoint = `${API_BASE_URL}/settings/project-categories/${id}`;
          refreshFn = fetchProjectCategories;
          break;
        case 'gccCountry':
        case 'otherCountry':
          endpoint = `${API_BASE_URL}/locations/${id}`;
          refreshFn = type === 'gccCountry' ? fetchGccCountries : fetchOtherCountries;
          break;
        default:
          return;
      }

      const response = await fetch(endpoint, { method: 'DELETE' });
      if (response.ok && refreshFn) {
        refreshFn();
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let endpoint = '';
      let refreshFn = null;
      let body = {};

      switch (modalType) {
        case 'category':
          endpoint = `${API_BASE_URL}/categories`;
          refreshFn = fetchCategories;
          const categorySlug = formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          body = { ...formData, slug: categorySlug };
          break;
        case 'subcategory':
          endpoint = `${API_BASE_URL}/categories/${parentCategory.id}/subcategories`;
          refreshFn = fetchCategories;
          const subSlug = formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now();
          body = { ...formData, slug: subSlug };
          break;
        case 'businessType':
          endpoint = `${API_BASE_URL}/settings/business-types`;
          refreshFn = fetchBusinessTypes;
          body = { name: formData.name };
          break;
        case 'salesCategory':
          endpoint = `${API_BASE_URL}/settings/sales-categories`;
          refreshFn = fetchSalesCategories;
          body = { name: formData.name };
          break;
        case 'projectCategory':
          endpoint = `${API_BASE_URL}/settings/project-categories`;
          refreshFn = fetchProjectCategories;
          body = { name: formData.name };
          break;
        case 'gccCountry':
          endpoint = `${API_BASE_URL}/locations`;
          refreshFn = fetchGccCountries;
          body = { 
            name: formData.name, 
            code: formData.code || formData.name.substring(0, 2).toUpperCase(),
            region: 'gcc',
            type: 'country'
          };
          break;
        case 'otherCountry':
          endpoint = `${API_BASE_URL}/locations`;
          refreshFn = fetchOtherCountries;
          body = { 
            name: formData.name, 
            code: formData.code || formData.name.substring(0, 2).toUpperCase(),
            region: 'other',
            type: 'country'
          };
          break;
        default:
          return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok && refreshFn) {
        refreshFn();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to save item');
      }
    } catch (error) {
      console.error(`Error saving ${modalType}:`, error);
    }
    
    setShowModal(false);
  };

  // Trash icon component
  const TrashIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.5 6H13V5.5C13 5.10218 12.842 4.72064 12.5607 4.43934C12.2794 4.15804 11.8978 4 11.5 4H8.5C8.10218 4 7.72064 4.15804 7.43934 4.43934C7.15804 4.72064 7 5.10218 7 5.5V6H4.5C4.36739 6 4.24021 6.05268 4.14645 6.14645C4.05268 6.24021 4 6.36739 4 6.5C4 6.63261 4.05268 6.75979 4.14645 6.85355C4.24021 6.94732 4.36739 7 4.5 7H5V16C5 16.2652 5.10536 16.5196 5.29289 16.7071C5.48043 16.8946 5.73478 17 6 17H14C14.2652 17 14.5196 16.8946 14.7071 16.7071C14.8946 16.5196 15 16.2652 15 16V7H15.5C15.6326 7 15.7598 6.94732 15.8536 6.85355C15.9473 6.75979 16 6.63261 16 6.5C16 6.36739 15.9473 6.24021 15.8536 6.14645C15.7598 6.05268 15.6326 6 15.5 6ZM8 5.5C8 5.36739 8.05268 5.24021 8.14645 5.14645C8.24021 5.05268 8.36739 5 8.5 5H11.5C11.6326 5 11.7598 5.05268 11.8536 5.14645C11.9473 5.24021 12 5.36739 12 5.5V6H8V5.5ZM14 16H6V7H14V16ZM9 9.5V13.5C9 13.6326 8.94732 13.7598 8.85355 13.8536C8.75979 13.9473 8.63261 14 8.5 14C8.36739 14 8.24021 13.9473 8.14645 13.8536C8.05268 13.7598 8 13.6326 8 13.5V9.5C8 9.36739 8.05268 9.24021 8.14645 9.14645C8.24021 9.05268 8.36739 9 8.5 9C8.63261 9 8.75979 9.05268 8.85355 9.14645C8.94732 9.24021 9 9.36739 9 9.5ZM12 9.5V13.5C12 13.6326 11.9473 13.7598 11.8536 13.8536C11.7598 13.9473 11.6326 14 11.5 14C11.3674 14 11.2402 13.9473 11.1464 13.8536C11.0527 13.7598 11 13.6326 11 13.5V9.5C11 9.36739 11.0527 9.24021 11.1464 9.14645C11.2402 9.05268 11.3674 9 11.5 9C11.6326 9 11.7598 9.05268 11.8536 9.14645C11.9473 9.24021 12 9.36739 12 9.5Z" fill="#DC3545"/>
    </svg>
  );

  // Plus icon component
  const PlusIcon = () => (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.375 10.625H8.125V16.875C8.125 17.0408 8.05915 17.1997 7.94194 17.3169C7.82473 17.4342 7.66576 17.5 7.5 17.5C7.33424 17.5 7.17527 17.4342 7.05806 17.3169C6.94085 17.1997 6.875 17.0408 6.875 16.875V10.625H0.625C0.45924 10.625 0.300269 10.5592 0.183058 10.4419C0.0658481 10.3247 0 10.1658 0 10C0 9.83424 0.0658481 9.67527 0.183058 9.55806C0.300269 9.44085 0.45924 9.375 0.625 9.375H6.875V3.125C6.875 2.95924 6.94085 2.80027 7.05806 2.68306C7.17527 2.56585 7.33424 2.5 7.5 2.5C7.66576 2.5 7.82473 2.56585 7.94194 2.68306C8.05915 2.80027 8.125 2.95924 8.125 3.125V9.375H14.375C14.5408 9.375 14.6997 9.44085 14.8169 9.55806C14.9342 9.67527 15 9.83424 15 10C15 10.1658 14.9342 10.3247 14.8169 10.4419C14.6997 10.5592 14.5408 10.625 14.375 10.625Z" fill="#28A745"/>
    </svg>
  );

  // Three dots icon component
  const ThreeDotsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.5 3C6.5 2.60218 6.65804 2.22064 6.93934 1.93934C7.22064 1.65804 7.60218 1.5 8 1.5C8.39782 1.5 8.77936 1.65804 9.06066 1.93934C9.34196 2.22064 9.5 2.60218 9.5 3C9.5 3.39782 9.34196 3.77936 9.06066 4.06066C8.77936 4.34196 8.39782 4.5 8 4.5C7.60218 4.5 7.22064 4.34196 6.93934 4.06066C6.65804 3.77936 6.5 3.39782 6.5 3ZM6.5 8C6.5 7.60218 6.65804 7.22064 6.93934 6.93934C7.22064 6.65804 7.60218 6.5 8 6.5C8.39782 6.5 8.77936 6.65804 9.06066 6.93934C9.34196 7.22064 9.5 7.60218 9.5 8C9.5 8.39782 9.34196 8.77936 9.06066 9.06066C8.77936 9.34196 8.39782 9.5 8 9.5C7.60218 9.5 7.22064 9.34196 6.93934 9.06066C6.65804 8.77936 6.5 8.39782 6.5 8ZM6.5 13C6.5 12.6022 6.65804 12.2206 6.93934 11.9393C7.22064 11.658 7.60218 11.5 8 11.5C8.39782 11.5 8.77936 11.658 9.06066 11.9393C9.34196 12.2206 9.5 12.6022 9.5 13C9.5 13.3978 9.34196 13.7794 9.06066 14.0607C8.77936 14.342 8.39782 14.5 8 14.5C7.60218 14.5 7.22064 14.342 6.93934 14.0607C6.65804 13.7794 6.5 13.3978 6.5 13Z" fill="#6D6D6D"/>
    </svg>
  );

  // Render list item with delete button
  const renderListItem = (item, type) => (
    <div key={item.id} className="lc-list-item">
      <span className="lc-item-name">{item.name}</span>
      <button className="lc-delete-icon" onClick={() => handleDelete(type, item.id)} title="Delete">
        <TrashIcon />
      </button>
    </div>
  );

  // Render add more button
  const renderAddMore = (type) => (
    <button className="lc-add-more-btn" onClick={() => openAddModal(type)}>
      <PlusIcon />
      Add more
    </button>
  );

  return (
    <AdminLayout>
      <div className="location-category-content">
        {/* Page Header */}
        <div className="lc-page-header">
          <h1 className="lc-page-title">Location & Category</h1>
          <p className="lc-page-desc">Add your business location and category to help suppliers and buyers connect and do business faster.</p>
        </div>

        {/* Top Row - Business Type & Specialists Category */}
        <div className="lc-top-row">
          {/* Business Type */}
          <div className="lc-section-card">
            <h3 className="lc-section-card-title">Business type</h3>
            <div className="lc-items-container">
              {businessTypes.map(item => renderListItem(item, 'businessType'))}
              {renderAddMore('businessType')}
            </div>
          </div>

          {/* Specialists Category - combining Sales and Project Categories */}
          <div className="lc-section-card">
            <h3 className="lc-section-card-title">Specialists Category</h3>
            <div className="lc-two-columns">
              <div className="lc-column">
                {salesCategories.map(item => renderListItem(item, 'salesCategory'))}
                {renderAddMore('salesCategory')}
              </div>
              <div className="lc-column">
                {projectCategories.map(item => renderListItem(item, 'projectCategory'))}
                {renderAddMore('projectCategory')}
              </div>
            </div>
          </div>
        </div>

        {/* Country Section */}
        <div className="lc-country-section">
          <h3 className="lc-section-header">Country</h3>
          <div className="lc-country-wrapper">
            <div className="lc-country-row">
              {/* GCC Countries Card */}
              <div className="lc-section-card">
                <div className="lc-gcc-inner-card">
                  <h4 className="lc-section-card-title">GCC Countries</h4>
                  <div className="lc-items-container">
                    {gccCountries.map(item => renderListItem(item, 'gccCountry'))}
                    {renderAddMore('gccCountry')}
                  </div>
                </div>
              </div>

              {/* Globally Card */}
              <div className="lc-section-card">
                <h4 className="lc-section-card-title">Globally</h4>
                <div className="lc-two-columns">
                  <div className="lc-column">
                    {otherCountries.slice(0, Math.ceil(otherCountries.length / 2)).map(item => renderListItem(item, 'otherCountry'))}
                  </div>
                  <div className="lc-column">
                    {otherCountries.slice(Math.ceil(otherCountries.length / 2)).map(item => renderListItem(item, 'otherCountry'))}
                  </div>
                </div>
                {renderAddMore('otherCountry')}
              </div>
            </div>
          </div>
        </div>

        {/* Category Section */}
        <div className="lc-category-section">
          <div className="lc-section-header-row">
            <h3 className="lc-section-header">Category</h3>
            <button className="lc-add-category-btn" onClick={() => openAddModal('category')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 12C21 12.1989 20.921 12.3897 20.7803 12.5303C20.6397 12.671 20.4489 12.75 20.25 12.75H12.75V20.25C12.75 20.4489 12.671 20.6397 12.5303 20.7803C12.3897 20.921 12.1989 21 12 21C11.8011 21 11.6103 20.921 11.4697 20.7803C11.329 20.6397 11.25 20.4489 11.25 20.25V12.75H3.75C3.55109 12.75 3.36032 12.671 3.21967 12.5303C3.07902 12.3897 3 12.1989 3 12C3 11.8011 3.07902 11.6103 3.21967 11.4697C3.36032 11.329 3.55109 11.25 3.75 11.25H11.25V3.75C11.25 3.55109 11.329 3.36032 11.4697 3.21967C11.6103 3.07902 11.8011 3 12 3C12.1989 3 12.3897 3.07902 12.5303 3.21967C12.671 3.36032 12.75 3.55109 12.75 3.75V11.25H20.25C20.4489 11.25 20.6397 11.329 20.7803 11.4697C20.921 11.6103 21 11.8011 21 12Z" fill="#28A745"/>
              </svg>
              Add more
            </button>
          </div>

          {loading ? (
            <div className="lc-loading">Loading categories...</div>
          ) : (
            <div className="lc-category-wrapper">
              <div className="lc-category-grid">
                {categories.map(category => (
                  <div key={category.id} className="lc-category-card">
                    <div className="lc-category-card-header">
                      <div className="lc-category-info">
                        {category.image ? (
                          <img src={category.image} alt={category.name} className="lc-category-img" />
                        ) : (
                          <div className="lc-category-img-placeholder">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CCC" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21,15 16,10 5,21"/>
                            </svg>
                          </div>
                        )}
                        <h4 className="lc-category-name">{category.name}</h4>
                      </div>
                      <button 
                        className="lc-delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete('category', category.id);
                        }}
                        title="Delete category"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                    
                    <div className="lc-subcategory-list">
                      {category.subcategories && category.subcategories.length > 0 ? (
                        category.subcategories.map((sub) => (
                          <div key={sub.id} className="lc-subcategory-item">
                            <span className="lc-sub-name">{sub.name}</span>
                            <button 
                              className="lc-delete-icon" 
                              onClick={() => handleDelete('subcategory', sub.id)}
                              title="Delete subcategory"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="lc-no-subcategories">No subcategories</p>
                      )}
                    </div>
                    
                    <button 
                      className="lc-add-subcategory-btn"
                      onClick={() => openAddModal('subcategory', category)}
                    >
                      <PlusIcon />
                      Add more
                    </button>
                  </div>
                ))}

                {/* Add New Category Card */}
                <div className="lc-add-category-card" onClick={() => openAddModal('category')}>
                  <div className="lc-add-image-box">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 8V40M40 24H8" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Modal */}
      {showModal && (
        <div className="lc-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="lc-modal" onClick={e => e.stopPropagation()}>
            <div className="lc-modal-header">
              <h2>
                {modalMode === 'add' ? 'Add' : 'Edit'} {
                  modalType === 'businessType' ? 'Business Type' :
                  modalType === 'salesCategory' ? 'Sales Category' :
                  modalType === 'projectCategory' ? 'Project Category' :
                  modalType === 'gccCountry' ? 'GCC Country' :
                  modalType === 'otherCountry' ? 'Country' :
                  modalType === 'category' ? 'Category' :
                  modalType === 'subcategory' ? 'Sub Category' : 'Item'
                }
              </h2>
              <button className="lc-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="lc-modal-form">
              <div className="lc-form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter name..."
                />
              </div>

              {(modalType === 'gccCountry' || modalType === 'otherCountry') && (
                <div className="lc-form-group">
                  <label>Country Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                    placeholder="e.g., US, IN, AE..."
                    maxLength={3}
                  />
                </div>
              )}

              {(modalType === 'category' || modalType === 'subcategory') && (
                <div className="lc-form-group">
                  <label>Image URL</label>
                  <input
                    type="text"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://..."
                  />
                  {formData.image && (
                    <img src={formData.image} alt="Preview" className="lc-image-preview" />
                  )}
                </div>
              )}

              <div className="lc-modal-actions">
                <button type="button" className="lc-btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="lc-btn-save">
                  {modalMode === 'add' ? 'Add' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default LocationCategoryPage;
